import { useEffect, useState, useRef } from 'react'
import axios from 'axios'

const GOLD = '#C29717'
const apiHost = process.env.REACT_APP_APIHOST || 'http://localhost:3033'
const authHdr = () => ({ Authorization: 'Bearer ' + (localStorage.getItem('token') || '') })

// Coverage options — GCC (one billable unit, 6 states), Syria, Lebanon only.
const COVERAGE = ['GCC', 'Syria', 'Lebanon']
const GCC_STATES = ['Saudi Arabia', 'UAE', 'Kuwait', 'Bahrain', 'Qatar', 'Oman']
const money = (cents, cur = 'usd') => (String(cur).toUpperCase() === 'USD' ? '$' : '') + ((cents || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })
const fdate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'

let _stripeJs
const loadStripeJs = () => _stripeJs || (_stripeJs = new Promise((res) => {
  if (window.Stripe) return res()
  const s = document.createElement('script'); s.src = 'https://js.stripe.com/v3/'; s.onload = res; document.head.appendChild(s)
}))

const Shell = ({ title, sub, onClose, children, wide }) => (
  <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(6,6,8,0.72)', zIndex: 100000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '48px 16px', backdropFilter: 'blur(3px)' }}>
    <div onClick={e => e.stopPropagation()} style={{ background: '#1c1c20', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, width: wide ? 760 : 560, maxWidth: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.6)', color: '#e6e6e6', fontFamily: 'Inter' }}>
      <div style={{ padding: '18px 22px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div><div style={{ fontSize: 16, fontWeight: 600 }}>{title}</div>{sub && <div style={{ fontSize: 12, color: '#9a9a9a', marginTop: 2 }}>{sub}</div>}</div>
        <div onClick={onClose} style={{ cursor: 'pointer', color: '#888', fontSize: 20, lineHeight: 1, padding: 4 }}>×</div>
      </div>
      <div style={{ padding: 22 }}>{children}</div>
    </div>
  </div>
)

export function UpgradePopup({ onClose }) {
  const [cfg, setCfg] = useState(null)
  const [plan, setPlan] = useState('essential')
  const [units, setUnits] = useState([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [done, setDone] = useState(false)
  const [cardReady, setCardReady] = useState(false)
  const cardRef = useRef(null)
  const stripeRef = useRef(null)
  const cardElRef = useRef(null)

  useEffect(() => {
    axios.get(apiHost + '/billing/config', { headers: authHdr() }).then(r => {
      const d = r.data; setCfg(d)
      setUnits(d.subscription && d.subscription.units && d.subscription.units.length ? d.subscription.units : ['GCC'])
      if (d.subscription && d.subscription.plan) setPlan(d.subscription.plan)
    }).catch(() => setErr('Could not load pricing.'))
  }, [])

  // Mount the inline Stripe card field (stays on the FORTE page — no redirect)
  useEffect(() => {
    if (!cfg || !cfg.publishableKey || done) return
    let cancelled = false
    loadStripeJs().then(() => {
      if (cancelled || !cardRef.current || cardElRef.current) return
      const stripe = window.Stripe(cfg.publishableKey)
      stripeRef.current = stripe
      const elements = stripe.elements()
      const card = elements.create('card', { style: { base: { color: '#e9e9e9', fontFamily: 'Inter, sans-serif', fontSize: '14px', '::placeholder': { color: '#777' } }, invalid: { color: '#e55' } } })
      card.mount(cardRef.current)
      card.on('ready', () => setCardReady(true))
      card.on('change', (ev) => setErr(ev.error ? ev.error.message : ''))
      cardElRef.current = card
    })
    return () => { cancelled = true }
  }, [cfg, done])

  const rate = cfg && cfg.plans && cfg.plans[plan] ? cfg.plans[plan].unitAmount : 0
  const total = rate * units.length
  const toggle = (u) => setUnits(p => p.includes(u) ? p.filter(x => x !== u) : [...p, u])

  const pay = async () => {
    setErr('')
    if (!units.length) { setErr('Select at least one country.'); return }
    if (!stripeRef.current || !cardElRef.current) { setErr('Payment form still loading…'); return }
    setBusy(true)
    try {
      const r = await axios.post(apiHost + '/billing/subscribe', { plan, units }, { headers: authHdr() })
      const cs = r.data && r.data.clientSecret
      if (!cs) { setErr('Could not start payment.'); setBusy(false); return }
      const result = await stripeRef.current.confirmCardPayment(cs, { payment_method: { card: cardElRef.current } })
      if (result.error) { setErr(result.error.message || 'Payment failed.'); setBusy(false); return }
      await axios.post(apiHost + '/billing/sync', {}, { headers: authHdr() }).catch(() => {})
      setDone(true)
    } catch (e) { setErr((e.response && e.response.data && e.response.data.errorMessage) || 'Payment failed.'); setBusy(false) }
  }

  const planCard = (id, label, desc) => {
    const active = plan === id; const r = cfg && cfg.plans[id] ? cfg.plans[id].unitAmount : 0
    return (
      <div onClick={() => setPlan(id)} style={{ flex: 1, cursor: 'pointer', border: '1px solid ' + (active ? GOLD : 'rgba(255,255,255,0.12)'), background: active ? 'rgba(194,151,23,0.08)' : 'transparent', borderRadius: 6, padding: '14px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div style={{ fontWeight: 600, color: active ? GOLD : '#e6e6e6', fontSize: 14 }}>{label}</div>
          <div style={{ fontSize: 12, color: '#bbb' }}>{money(r)}<span style={{ color: '#777' }}>/country/mo</span></div>
        </div>
        <div style={{ fontSize: 11.5, color: '#9a9a9a', marginTop: 6, lineHeight: 1.5 }}>{desc}</div>
      </div>
    )
  }

  if (done) {
    return (
      <Shell title="Payment successful" onClose={onClose}>
        <div style={{ textAlign: 'center', padding: '10px 0 6px' }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>✓</div>
          <div style={{ fontSize: 14.5, color: '#d8d8d8', lineHeight: 1.6, marginBottom: 20 }}>Your subscription is active. The map and intelligence feed are now fully unlocked for your selected coverage.</div>
          <button onClick={() => window.location.reload()} style={{ background: GOLD, color: '#1a1a1a', border: 'none', borderRadius: 5, padding: '11px 26px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Continue</button>
        </div>
      </Shell>
    )
  }

  return (
    <Shell title="Upgrade your coverage" sub="Choose a plan and the countries you want to monitor" onClose={onClose} wide>
      {!cfg ? <div style={{ color: '#888', padding: '20px 0' }}>Loading…</div> : (
        <>
          <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
            {planCard('essential', 'Essential', 'Live map & events, real-time incident alerts, monthly intelligence reports.')}
            {planCard('complete', 'Complete', 'Everything in Essential + weekly reports with risk assessments, direct PDF, localized alerts.')}
          </div>

          <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Countries covered <span style={{ color: '#666', textTransform: 'none', letterSpacing: 0 }}>· GCC counts as one</span></div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
            {COVERAGE.map(u => {
              const on = units.includes(u)
              return (
                <span key={u} onClick={() => toggle(u)} style={{ cursor: 'pointer', userSelect: 'none', fontSize: 12.5, padding: '7px 14px', borderRadius: 20, border: '1px solid ' + (on ? GOLD : 'rgba(255,255,255,0.14)'), background: on ? 'rgba(194,151,23,0.14)' : 'transparent', color: on ? GOLD : '#bbb', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {on ? '✓' : '+'} {u}
                </span>
              )
            })}
          </div>
          <div style={{ fontSize: 11, color: '#8a8a8a', marginBottom: 18 }}>GCC includes {GCC_STATES.join(', ')}.</div>

          {/* Inline card field — payment happens on the FORTE platform */}
          <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Card details</div>
          <div style={{ background: '#141417', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, padding: '13px 14px', marginBottom: 4 }}>
            <div ref={cardRef} />
            {!cardReady && <div style={{ fontSize: 12, color: '#777' }}>Loading secure card field…</div>}
          </div>
          <div style={{ fontSize: 10.5, color: '#777', marginBottom: 18 }}>🔒 Secured by Stripe. Your card is entered directly here — you stay on FORTE.</div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 16 }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>{money(total)}<span style={{ fontSize: 13, color: '#888', fontWeight: 400 }}> /month</span></div>
              <div style={{ fontSize: 11.5, color: '#9a9a9a' }}>{units.length} {units.length === 1 ? 'country' : 'countries'} × {money(rate)} · billed monthly</div>
            </div>
            <button onClick={pay} disabled={busy || !cfg.billingConfigured} style={{ background: GOLD, color: '#1a1a1a', border: 'none', borderRadius: 5, padding: '12px 34px', fontSize: 14, fontWeight: 700, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.7 : 1, letterSpacing: 0.3 }}>
              {busy ? 'Processing…' : 'Pay'}
            </button>
          </div>
          {!cfg.billingConfigured && <div style={{ fontSize: 11, color: '#e88a2a', marginTop: 10 }}>Billing is not fully configured yet.</div>}
          {err && <div style={{ fontSize: 12, color: '#e55', marginTop: 10 }}>{err}</div>}
        </>
      )}
    </Shell>
  )
}

export function BillingSettings({ onClose, onManageCountries }) {
  const [d, setD] = useState(null)
  const [portalBusy, setPortalBusy] = useState(false)
  useEffect(() => { axios.get(apiHost + '/billing/status', { headers: authHdr() }).then(r => setD(r.data)).catch(() => setD({ error: true })) }, [])
  const portal = async () => { setPortalBusy(true); try { const r = await axios.post(apiHost + '/billing/portal', {}, { headers: authHdr() }); if (r.data.url) window.location.href = r.data.url } catch (e) {} setPortalBusy(false) }

  const s = d && d.subscription
  const statusColor = { active: '#4CAF50', trialing: '#C29717', past_due: '#e55', canceled: '#888' }[(s && s.status) || ''] || '#888'
  return (
    <Shell title="Billing & invoices" sub="Subscription, payments and coverage" onClose={onClose} wide>
      {!d ? <div style={{ color: '#888' }}>Loading…</div> : d.error ? <div style={{ color: '#e55' }}>Could not load billing.</div> : (
        <>
          <div style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
            {[['Status', <span style={{ color: statusColor, textTransform: 'capitalize' }}>{(s.status || 'none').replace('_', ' ')}</span>],
              ['Plan', s.plan ? s.plan[0].toUpperCase() + s.plan.slice(1) : '—'],
              ['Countries', s.unitCount || 0],
              ['Monthly', money(s.monthlyAmount)],
              ['Next payment', fdate(s.currentPeriodEnd)],
              ['Days left', d.daysLeft != null ? d.daysLeft : '—']].map(([k, v]) => (
              <div key={k} style={{ flex: '1 0 30%', minWidth: 120, background: '#161619', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 6, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>{k}</div>
                <div style={{ fontSize: 15, fontWeight: 600, marginTop: 3 }}>{v}</div>
              </div>
            ))}
          </div>
          {s.coveredCountries && s.coveredCountries.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Covered</div>
              <div style={{ fontSize: 12.5, color: '#ccc' }}>{s.coveredCountries.join(', ')}</div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
            <button onClick={onManageCountries} style={{ background: GOLD, color: '#1a1a1a', border: 'none', borderRadius: 5, padding: '9px 18px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Change plan / countries</button>
            {(s.status && s.status !== 'none') ? <button onClick={portal} disabled={portalBusy} style={{ background: 'transparent', color: '#ccc', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 5, padding: '9px 18px', fontSize: 12, cursor: 'pointer' }}>{portalBusy ? '…' : 'Manage subscription'}</button> : null}
          </div>
          <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Invoices</div>
          {(!d.invoices || !d.invoices.length) ? <div style={{ fontSize: 12, color: '#888' }}>No invoices yet.</div> : (
            <div style={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: 6, overflow: 'hidden' }}>
              {d.invoices.map((i, idx) => (
                <div key={i.id || idx} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderTop: idx ? '1px solid rgba(255,255,255,0.04)' : 'none', fontSize: 12 }}>
                  <div style={{ width: 90, color: '#bbb' }}>{fdate(i.date)}</div>
                  <div style={{ width: 70, fontWeight: 600 }}>{money(i.amount, i.currency)}</div>
                  <div style={{ flex: 1, textTransform: 'capitalize', color: i.status === 'paid' ? '#4CAF50' : i.status === 'payment_failed' ? '#e55' : '#e88a2a' }}>{(i.status || '').replace('_', ' ')}</div>
                  {i.url && <a href={i.url} target="_blank" rel="noreferrer" style={{ color: GOLD, textDecoration: 'none' }}>Receipt →</a>}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Shell>
  )
}

// Full-cockpit blur when the account is no longer entitled (trial ended, unpaid / past_due).
export function LockOverlay({ onUpgrade, status }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 90000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(10,10,12,0.55)', backdropFilter: 'blur(9px)' }}>
      <div style={{ textAlign: 'center', maxWidth: 460, padding: 30 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#fff', marginBottom: 10 }}>{status === 'past_due' ? 'Your payment failed' : 'Your access has ended'}</div>
        <div style={{ fontSize: 13.5, color: '#c3c3c3', lineHeight: 1.6, marginBottom: 22 }}>
          {status === 'past_due'
            ? 'We could not process your last payment, so the intelligence feed and map are locked. Update your payment to restore access.'
            : 'Your free trial has ended. Subscribe to keep live access to the map, events and intelligence reports.'}
        </div>
        <button onClick={onUpgrade} style={{ background: GOLD, color: '#1a1a1a', border: 'none', borderRadius: 5, padding: '13px 30px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', letterSpacing: 0.4 }}>
          {status === 'past_due' ? 'Update payment' : 'Choose a plan'}
        </button>
      </div>
    </div>
  )
}
